import { useCallback, useContext, useEffect, useRef } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { GlobalContext } from "../Context/GlobalContext";

const LOGOUT_EVENT_KEY = "chaafo_logout_event";

export const AUTH_STORAGE_KEYS = [
  // Current GlobalContext key
  "chaafoUser",

  // Customer identity
  "user_code",
  "userCode",
  "customerId",
  "customer_id",
  "chaafoCustomerId",

  // Stored user/customer objects
  "user",
  "customer",
  "customerData",
  "loggedInCustomer",
  "chaafoCustomer",
  "userData",
  "loginData",
  "loginResponse",
  "customerDetails",
  "customerInfo",

  // Profile details
  "first_name",
  "firstName",
  "last_name",
  "lastName",
  "email",
  "phone",
  "number",
  "mobile",
  "customerMobile",
  "date_of_birth",
  "privacy_policy",

  // Restaurant and delivery session
  "selectedDeliveryAddress",
  "deliveryAddress",
  "selectedRestaurant",
  "selectedRestaurantUserId",
  "restaurantUserId",
  "selectedOrderType",
  "selectedPlatform",

  // Token-based auth fallback
  "token",
  "accessToken",
  "access_token",
  "authToken",
  "refreshToken",
  "isLoggedIn",
];

const normalizeString = (value) => String(value ?? "").trim();

const safelyParseJson = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const getStorageValue = (storage, key) => {
  try {
    return normalizeString(storage.getItem(key));
  } catch {
    return "";
  }
};

const hasObjectSession = (storage) => {
  const objectKeys = [
    "chaafoUser",
    "user",
    "customer",
    "customerData",
    "loggedInCustomer",
    "chaafoCustomer",
    "userData",
    "loginData",
    "loginResponse",
  ];

  return objectKeys.some((key) => {
    const parsedValue = safelyParseJson(storage.getItem(key));

    return Boolean(
      parsedValue?.user_code ||
      parsedValue?.userCode ||
      parsedValue?.number ||
      parsedValue?.customerId ||
      parsedValue?.customer_id ||
      parsedValue?.response?.user_code ||
      parsedValue?.response?.customerId ||
      parsedValue?.data?.user_code ||
      parsedValue?.data?.customerId,
    );
  });
};

export const hasStoredAuthSession = () => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const directSessionKeys = [
      "user_code",
      "userCode",
      "customerId",
      "customer_id",
      "chaafoCustomerId",
    ];

    const hasDirectSession = directSessionKeys.some(
      (key) =>
        Boolean(getStorageValue(sessionStorage, key)) ||
        Boolean(getStorageValue(localStorage, key)),
    );

    return (
      hasDirectSession ||
      hasObjectSession(sessionStorage) ||
      hasObjectSession(localStorage)
    );
  } catch {
    return false;
  }
};

export const clearAuthStorage = () => {
  if (typeof window === "undefined") {
    return;
  }

  [sessionStorage, localStorage].forEach((storage) => {
    AUTH_STORAGE_KEYS.forEach((key) => {
      try {
        storage.removeItem(key);
      } catch (error) {
        console.error(`Unable to remove ${key} from storage:`, error);
      }
    });
  });
};

export const broadcastLogout = () => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(LOGOUT_EVENT_KEY, String(Date.now()));
  } catch (error) {
    console.error("Unable to broadcast logout:", error);
  }
};

export const requestLogout = () => {
  if (typeof window === "undefined") {
    return;
  }

  clearAuthStorage();
  broadcastLogout();

  window.dispatchEvent(new CustomEvent("auth:logout"));
};

export default function LogoutWatcher() {
  const navigate = useNavigate();
  const location = useLocation();

  const globalContext = useContext(GlobalContext);

  const { user, authLoading, logoutUser, clearSelectedRestaurant } =
    globalContext || {};

  const sessionWasAvailableRef = useRef(false);
  const logoutInProgressRef = useRef(false);

  const completeLogout = useCallback(() => {
    if (logoutInProgressRef.current) {
      return;
    }

    logoutInProgressRef.current = true;
    logoutUser?.();
    clearSelectedRestaurant?.();
    clearAuthStorage();

    if (location.pathname !== "/login") {
      navigate("/login", {
        replace: true,
        state: {
          loggedOut: true,
        },
      });
    }

    window.setTimeout(() => {
      
      clearAuthStorage();
      logoutInProgressRef.current = false;
    }, 0);
  }, [clearSelectedRestaurant, location.pathname, logoutUser, navigate]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (user || hasStoredAuthSession()) {
      sessionWasAvailableRef.current = true;
    }
  }, [authLoading, user]);

  useEffect(() => {
    const handleManualLogout = () => {
      completeLogout();
    };

    const handleStorageChange = (event) => {
     
      if (event.key === LOGOUT_EVENT_KEY) {
        completeLogout();
        return;
      }

     
      if (event.key === null) {
        if (sessionWasAvailableRef.current) {
          completeLogout();
        }

        return;
      }

      if (!AUTH_STORAGE_KEYS.includes(event.key)) {
        return;
      }

      if (!hasStoredAuthSession()) {
        completeLogout();
      }
    };

    const verifyCurrentSession = () => {
      if (authLoading || !sessionWasAvailableRef.current) {
        return;
      }

      if (!hasStoredAuthSession()) {
        completeLogout();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        verifyCurrentSession();
      }
    };

    window.addEventListener("auth:logout", handleManualLogout);

    window.addEventListener("storage", handleStorageChange);

    window.addEventListener("focus", verifyCurrentSession);

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("auth:logout", handleManualLogout);

      window.removeEventListener("storage", handleStorageChange);

      window.removeEventListener("focus", verifyCurrentSession);

      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [authLoading, completeLogout]);

  return null;
}
